import React, { ComponentProps, useEffect } from 'react';
import clsx from 'clsx';
import { useTranslations } from '../../../hooks/useTranslations';
import {
  useAnalyticsPost,
  NarvarEventTypes,
  NarvarEventComponents,
} from '../../../hooks/Analytics';
import Panel from '../../ui/Panel/Panel';
import PanelHeader from '../../ui/PanelHeader/PanelHeader';
import Edd from './EddBlock/BlockEdd';
import TrackingStatus, { TrackingStatusProps } from './TrackingStatus';
import ShippingActivity, {
  ShippingActivityProps,
} from './ShippingActivity/ShippingActivity';
import Divider from '../../ui/Divider/Divider';
import Paragraph from '../../ui/Paragraph/Paragraph';
import CarrierTrackingLink from './CarrierTrackingLink';
import VPOD from './Vpod/Vpod';
import { LayoutTree } from '../DynamicLayout';
import { usePageContext } from '../../../hooks/PageContext/usePageContext';
import useOrderTracking from '../../../hooks/useOrderTracking';
import useEddTimeRange from './hooks/useEddTimeRange';
import styles from './EddPanel.module.css';

const { FeatureLayout } = LayoutTree;

export interface PackEddPanelProps
  extends
    ComponentProps<typeof Edd>,
    TrackingStatusProps,
    ShippingActivityProps {
  layout?: LayoutTree.FeatureLayout;
  fixedPanelHeight?: string;
  featureName?: string;
}

const PackEddPanel = ({
  featureName,
  layout = FeatureLayout.PORTRAIT,
  fixedPanelHeight,
}: PackEddPanelProps) => {
  const { packEddPanelFallbackMessage, packEddPanelTitleText } =
    useTranslations();

  const isLandscape = layout === FeatureLayout.LANDSCAPE;
  let isDelivered = false;

  const {
    state: { orderNumbers },
  } = usePageContext();

  const { getCurrentShipment } = useOrderTracking();
  const currentShipment = getCurrentShipment();

  const logEvent = useAnalyticsPost();

  const formattedEddTimeRange = useEddTimeRange();

  useEffect(() => {
    const orderNumber = (orderNumbers && orderNumbers[0]) || null;

    logEvent({
      eventComponent: NarvarEventComponents.NTH_PACK_EDD_PANEL,
      eventFeature: NarvarEventComponents.NTH_PACK_EDD_PANEL,
      eventType: NarvarEventTypes.COMPONENT_RENDER,
      feature: featureName,
      orderNumber,
      pageComponent: NarvarEventComponents.NTH_PACK_EDD_PANEL,
      pageOrderNumber: orderNumber,
      trackLayout: layout,
      displayedEddBegin: currentShipment?.displayDateRange?.begin,
      displayedEddEnd: currentShipment?.displayDateRange?.end,
      eddTimeRangeBegin: currentShipment?.eddTimeRange?.[0],
      eddTimeRangeEnd: currentShipment?.eddTimeRange?.[1],
      eddDisplayedTimeRange: formattedEddTimeRange?.join(' - '),
    });

    // log for new edd vs old edd metrics, since component render gets lumped
    logEvent({
      eventComponent: NarvarEventComponents.NTH_PACK_EDD_PANEL,
      eventType: NarvarEventTypes.FEATURE_USAGE,
      feature: featureName,
      orderNumber,
      pageComponent: NarvarEventComponents.NTH_PACK_EDD_PANEL,
      pageOrderNumber: orderNumber,
      trackLayout: layout,
      displayedEddBegin: currentShipment?.displayDateRange?.begin,
      displayedEddEnd: currentShipment?.displayDateRange?.end,
      eddTimeRangeBegin: currentShipment?.eddTimeRange?.[0],
      eddTimeRangeEnd: currentShipment?.eddTimeRange?.[1],
      eddDisplayedTimeRange: formattedEddTimeRange?.join(' - '),
    });
  }, [currentShipment, featureName, layout, logEvent, orderNumbers]);

  // We show the fallback view if there's no shipment available
  const { isFallback, trackingNumber, status } = currentShipment ?? {
    isFallback: true,
  };

  if (status === 'DELIVERED') isDelivered = true;

  const columnLeftClasses = clsx({
    [styles.columnLeft]: !isLandscape,
    [styles.columnLeftLandscape]: isLandscape,
    [styles.eddColumn]: !isDelivered,
    [styles.eddColumnReverse]: isDelivered,
  });

  const columnRightClasses = clsx(
    !isLandscape && styles.columnRight,
    isLandscape && styles.columnRightLandscape,
  );

  const validShipmentColumns = (
    <>
      <div className={columnLeftClasses}>
        <TrackingStatus featureName={featureName} layout={layout} />
        <Edd orderNumbers={orderNumbers} featureName={featureName} />
      </div>
      <div className={columnRightClasses}>
        <VPOD layout={layout} featureName={featureName} />
        <ShippingActivity featureName={featureName} layout={layout} />
      </div>
    </>
  );

  const fallbackShipmentColumns = (
    <>
      <div className={columnLeftClasses}>
        <Paragraph
          containerCustomStyle={{
            marginTop: 0,
            marginBottom: isLandscape && trackingNumber ? undefined : 0,
          }}
        >
          {packEddPanelFallbackMessage}
        </Paragraph>
      </div>
      {trackingNumber && (
        <div className={columnRightClasses}>
          {!isLandscape && <Divider />}
          <CarrierTrackingLink featureName={featureName} layout={layout} />
        </div>
      )}
    </>
  );

  const columns = isFallback ? fallbackShipmentColumns : validShipmentColumns;

  const containerClasses = clsx(
    styles.container,
    isLandscape && styles.containerLandscape,
  );

  return (
    <Panel
      fixedPanelHeight={fixedPanelHeight}
      header={<PanelHeader>{packEddPanelTitleText}</PanelHeader>}
    >
      <div className={containerClasses}>{columns}</div>
    </Panel>
  );
};

export default PackEddPanel;
